import { useCallback, useMemo, useState } from 'react';

import { removeNullishFromObj } from '@src/shared/utils/helpers/remove-nullish-from-obj';
import type { TView } from '@src/shared/types/view';
import type { OnChangeFn } from '@tanstack/react-table';

export type TTiclickTableFilter = {
  staticFilters?: TView['static_filters'];
  dynamicFilters?: TView['filters'];
  customFieldFilters?: TView['custom_filters'];
};

export function useTableFilters(view?: TView) {
  const viewData = useMemo(
    () =>
      removeNullishFromObj<TTiclickTableFilter>({
        staticFilters: view?.static_filters,
        customFieldFilters: view?.custom_filters,
        dynamicFilters: view?.filters,
      }),
    [view?.static_filters, view?.custom_filters, view?.filters]
  );

  const [tableFilters, setTableFilters] = useState<
    TTiclickTableFilter | undefined
  >(viewData);

  const derivedViewFilters = tableFilters || viewData;

  const cleaned = useMemo(
    () => removeNullishFromObj<TTiclickTableFilter>(derivedViewFilters),
    [derivedViewFilters]
  );

  const onGlobalFilterChange: OnChangeFn<Record<string, unknown>> = useCallback(
    (updaterOrValue) => {
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(tableFilters || {})
          : updaterOrValue;

      setTableFilters(next);
    },
    [tableFilters, setTableFilters]
  );

  return {
    tableFilters: cleaned,
    onGlobalFilterChange,
  };
}
